// src/utils/tracking.js
// Centralized Mixpanel tracking utility with best practices
import mixpanel from "mixpanel-browser";
import { standardUserApi } from "../services/standardUserApi";
import { getOrCreateAnonId, getFbcId, getFbpId } from "./sourceTracking";

/**
 * Get database user ID if available
 * This is set globally by various components when user logs in
 */
export const getUserId = () => {
  return window.okgatherUserId || null;
};

/**
 * Set the user ID property in Mixpanel
 * This should be called when a user logs in or when their profile is loaded
 * Uses a non-blocking async approach to avoid impacting user experience
 */
export const setUserIdProperty = async () => {
  try {
    setTimeout(async () => {
      try {
        const token = localStorage.getItem("memberstackToken");
        if (!token || !mixpanel || !mixpanel.__loaded) return;

        const user = await Promise.race([
          standardUserApi.getUserProfile(token),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 2000)
          )
        ]);

        if (user && user.id && typeof user.id !== 'undefined') {
          // Set as user property instead of changing distinct ID
          mixpanel.people.set({
            'database_user_id': user.id,
            '$email': user.email
          });

          // Store globally for use in tracking calls
          window.okgatherUserId = user.id;
        }
      } catch (e) {
        // Silent failure - tracking should never block user experience
      }
    }, 0);
  } catch (e) {
    // Outer catch for safety
  }
};

const LANDING_PAGE_VARIANT_STORAGE_KEY = 'landing_page_variant';

/**
 * Register which A/B variant ('A' or 'B') a visitor was assigned on a theme
 * landing page. Persisted first-touch to localStorage (never overwritten)
 * so it survives a full page reload and can be sent to the backend at
 * signup/checkout - see sourceTracking.js's captureLandingPage for why that
 * matters. Also attached as a Mixpanel super property/first-touch people
 * property, independent of whether localStorage already had a value, so
 * Mixpanel-only reporting keeps working exactly as before.
 */
export const registerLandingPageVariant = (variant) => {
  if (!variant) return;

  if (!localStorage.getItem(LANDING_PAGE_VARIANT_STORAGE_KEY)) {
    localStorage.setItem(LANDING_PAGE_VARIANT_STORAGE_KEY, variant);
  }

  try {
    if (!mixpanel || !mixpanel.__loaded) return;
    mixpanel.register({ landing_page_variant: variant });
    mixpanel.people.set_once({ first_landing_page_variant: variant });
  } catch (error) {
    console.warn('Mixpanel variant registration error:', error);
  }
};

/**
 * Records a first-party page_views row (see models.PageView's docstring)
 * alongside whatever Mixpanel event the caller already fires - a reliable
 * floor for the Landing Page Performance tab's "views" stage, since a
 * fetch() to this app's own domain isn't caught by the ad-blocker/tracking
 * -protection lists that commonly target Mixpanel's third-party domain.
 * Gated on the same `mixpanel.__loaded` consent check as trackEvent() below
 * - this exists to catch traffic Mixpanel's own delivery misses, not to
 * track visitors who declined tracking altogether.
 */
export const recordPageView = ({ landingPage, variant, source } = {}) => {
  try {
    if (!mixpanel || !mixpanel.__loaded) return;
    if (!landingPage) return;
    fetch('/api/tracking/page-view', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anon_id: getOrCreateAnonId(),
        landing_page: landingPage,
        landing_page_variant: variant || null,
        traffic_source: source || null,
      }),
    }).catch(() => {});
  } catch (error) {
    // Silent failure - tracking should never block user experience
  }
};

/**
 * Fire the Meta pixel ViewContent event + notify the backend
 * (/api/tracking/view-content, which also sends Meta CAPI - see
 * send_to_meta_capi in app.py) for a theme/blog landing page view. Unlike
 * the Purchase/CompleteRegistration events elsewhere in this file, the
 * visitor here is very often anonymous (pre-signup), so there's no
 * memberstackToken/user to attach - Meta CAPI match quality relies on
 * fbc/fbp/IP/UA instead (handled server-side).
 *
 * eventId is generated fresh per call so a re-mount of the same page (e.g.
 * React strict-mode double-invoke, or navigating away and back) doesn't
 * reuse a stale id - each ViewContent is its own event, there's no earlier
 * client-only fire to dedup against like there is for Purchase/StartTrial.
 */
export const reportViewContent = () => {
  if (!window.fbq) return;

  const eventId = `vc_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  window.fbq('track', 'ViewContent', {}, { eventID: eventId });

  fetch('/api/tracking/view-content', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ event_id: eventId }),
  }).catch(() => {});
};

/**
 * Fire the Meta pixel Purchase/StartTrial event + notify the backend
 * (/api/tracking/purchase-success, which also sends Meta CAPI - see
 * send_to_meta_capi in app.py) when this page load is a redirect back from
 * a successful Stripe checkout. Both MyEvents.js (the current Stripe
 * success target) and EditProfile.js (kept as a harmless safety net) call
 * this, so it's consolidated here rather than copy-pasted in both.
 *
 * cleanUrlPath is the path to reset the URL to afterwards (each caller's
 * own route), so the fromCheckout/success/session_id query params and
 * sessionStorage checkoutData aren't reprocessed on a later reload/back-nav.
 */
export const reportCheckoutSuccessConversion = async (cleanUrlPath) => {
  const urlParams = new URLSearchParams(window.location.search);
  const checkoutData = JSON.parse(sessionStorage.getItem('checkoutData') || '{}');

  const isSuccess = (
    urlParams.get('fromCheckout') === 'true' ||
    urlParams.get('success') === 'true' ||
    urlParams.get('session_id') ||
    (checkoutData.timestamp && Date.now() - checkoutData.timestamp < 600000)
  );

  if (!isSuccess || !checkoutData.eventId) {
    return;
  }

  const eventType = checkoutData.isFreeTrial ? 'StartTrial' : 'Purchase';

  if (window.fbq) {
    // IMPORTANT: eventID must be in the 4th parameter (options) - that's
    // what lets Meta dedup this browser event against the CAPI event the
    // backend call below sends with the same event_id.
    window.fbq('track', eventType, {
      value: checkoutData.value,
      currency: checkoutData.currency
    }, {
      eventID: checkoutData.eventId
    });
  }

  try {
    const token = localStorage.getItem("memberstackToken");
    if (token) {
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const fbcId = getFbcId();
      const fbpId = getFbpId();
      if (fbcId) headers['X-Fbc'] = fbcId;
      if (fbpId) headers['X-Fbp'] = fbpId;

      await fetch('/api/tracking/purchase-success', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          event_id: checkoutData.eventId,
          event_type: eventType,
          value: checkoutData.value,
          currency: checkoutData.currency,
          price_id: checkoutData.priceId
        })
      });
    }
  } catch (error) {
    console.warn('Backend tracking failed:', error);
  }

  sessionStorage.removeItem('checkoutData');
  localStorage.removeItem('referralCode');
  localStorage.removeItem('source_group');
  localStorage.removeItem('source_platform');
  localStorage.removeItem('source_placement');
  window.history.replaceState({}, document.title, cleanUrlPath);
};

/**
 * Get common properties to include in all events
 */
const getCommonProperties = () => {
  return {
    database_user_id: getUserId(),
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    page_path: window.location.pathname,
  };
};

/**
 * Track a Mixpanel event with standard properties
 *
 * @param {string} eventName - Name of the event (e.g., "Theme Landing: Viewed")
 * @param {object} properties - Additional properties specific to this event
 */
export const trackEvent = (eventName, properties = {}) => {
  try {
    if (mixpanel && mixpanel.__loaded) {
      const eventProperties = {
        ...getCommonProperties(),
        ...properties
      };

      mixpanel.track(eventName, eventProperties);
    }
  } catch (error) {
    console.warn('Mixpanel tracking error:', error);
  }
};

/**
 * Track a page view with context
 *
 * @param {string} pageName - Human-readable page name
 * @param {object} context - Additional context about the page
 */
export const trackPageView = (pageName, context = {}) => {
  trackEvent(`${pageName}: Viewed`, {
    page_name: pageName,
    ...context
  });
};

/**
 * Track a button/CTA click
 *
 * @param {string} buttonName - Name/label of the button
 * @param {string} location - Where on the page the button is (e.g., "hero", "footer")
 * @param {object} context - Additional context
 */
export const trackButtonClick = (buttonName, location, context = {}) => {
  trackEvent(`Button: Clicked`, {
    button_name: buttonName,
    button_location: location,
    ...context
  });
};

/**
 * Track theme-related events with theme metadata
 *
 * @param {string} action - The action being performed (e.g., "Viewed", "Selected")
 * @param {object} themeData - Theme data including id, name, type, etc.
 * @param {object} additionalProps - Any additional properties
 */
export const trackThemeEvent = (action, themeData = {}, additionalProps = {}) => {
  const { themeId, themeName, themeType, themeSlug } = themeData;

  trackEvent(`Theme: ${action}`, {
    theme_id: themeId || null,
    theme_name: themeName || null,
    theme_type: themeType || null,
    theme_slug: themeSlug || null,
    ...additionalProps
  });
};

/**
 * Track blog-related events with post metadata
 *
 * @param {string} action - The action being performed (e.g., "Viewed", "CTA Clicked")
 * @param {object} blogData - Blog data including slug and title
 * @param {object} additionalProps - Any additional properties
 */
export const trackBlogEvent = (action, blogData = {}, additionalProps = {}) => {
  const { blogSlug, blogTitle } = blogData;

  trackEvent(`Blog: ${action}`, {
    blog_slug: blogSlug || null,
    blog_title: blogTitle || null,
    ...additionalProps
  });
};

/**
 * Track user actions in forms
 *
 * @param {string} formName - Name of the form
 * @param {string} action - Action performed (e.g., "Viewed", "Submitted", "Error")
 * @param {object} context - Additional context
 */
export const trackFormEvent = (formName, action, context = {}) => {
  trackEvent(`Form: ${action}`, {
    form_name: formName,
    ...context
  });
};

/**
 * Track slot/gathering related events
 *
 * @param {string} action - Action performed (e.g., "Selected", "Deselected", "Booked")
 * @param {object} slotData - Slot/gathering data
 * @param {object} additionalProps - Additional properties
 */
export const trackSlotEvent = (action, slotData = {}, additionalProps = {}) => {
  const { slotId, themeId, themeName, datetime } = slotData;

  trackEvent(`Slot: ${action}`, {
    slot_id: slotId || null,
    theme_id: themeId || null,
    theme_name: themeName || null,
    slot_datetime: datetime || null,
    ...additionalProps
  });
};

/**
 * Track navigation events
 *
 * @param {string} from - Where the user is navigating from
 * @param {string} to - Where the user is navigating to
 * @param {object} context - Additional context
 */
export const trackNavigation = (from, to, context = {}) => {
  trackEvent('Navigation: Page Change', {
    from_page: from,
    to_page: to,
    ...context
  });
};

/**
 * Track errors and issues
 *
 * @param {string} errorType - Type of error
 * @param {string} errorMessage - Error message
 * @param {object} context - Additional context
 */
export const trackError = (errorType, errorMessage, context = {}) => {
  trackEvent('Error: Occurred', {
    error_type: errorType,
    error_message: errorMessage,
    ...context
  });
};

/**
 * Track conversion funnel steps
 *
 * @param {string} funnelName - Name of the funnel
 * @param {string} step - Current step in the funnel
 * @param {number} stepNumber - Step number (for ordering)
 * @param {object} context - Additional context
 */
export const trackFunnelStep = (funnelName, step, stepNumber, context = {}) => {
  trackEvent(`Funnel: ${funnelName}`, {
    funnel_step: step,
    funnel_step_number: stepNumber,
    ...context
  });
};

const trackingExports = {
  trackEvent,
  trackPageView,
  trackButtonClick,
  trackThemeEvent,
  trackFormEvent,
  trackSlotEvent,
  trackNavigation,
  trackError,
  trackFunnelStep,
  setUserIdProperty,
  getUserId,
  registerLandingPageVariant,
  recordPageView,
  reportCheckoutSuccessConversion,
  reportViewContent,
};

export default trackingExports;
